<template>
  <RegisterForm
    :title="$t('verificationStatus.idVerification.idUpload.title')"
    :nextButtonOnClick="nextButtonOnClick"
    :back-button-on-click="backButtonOnClick"
    verticalPadding="py-0"
  >
    <IdUploadComponent
      ref="form"
      :uploadInfoText="$t(`verificationStatus.idVerification.idUpload.uploadInfoText`)"
      :pendingReason="pendingReason"
      :isBackImageRequired="isBackImageRequired"
  /></RegisterForm>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { RegisterRoutePath } from '@/router/registerRoutes';
import { endAjaxLoading, startLoading } from '@/util/loading';
import registerStep, { getUserStep } from '@/constants/register/registerStep';
import auditStatus from '@/constants/auditStatus';
import fileType from '@/constants/fileType';
import auditType from '@/constants/register/auditType';
import registerRepository from '@/repositories/register/registerRepository';
import commonRepository from '@/repositories/commonRepository';
import poiStore from '@/store/register/poiVerification/poiStore';
import RegisterForm from '@/components/register/registerForms/RegisterForm.vue';
import IdUploadComponent from '@/components/register/registerForms/uploadForms/IdUpload.vue';
import { idType } from '@/components/register/registerForms/uploadForms/IdInformation.vue';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';

export default {
  name: 'IdUpload',
  components: { RegisterForm, IdUploadComponent },
  computed: {
    idTypeData() {
      return this.idInformationForm?.idType ?? null;
    },
    pendingReason() {
      return this.poiAuditStatus === auditStatus.pending
        ? poiStore.state.poiCustomPendingReason?.length > 0
          ? poiStore.state.poiCustomPendingReason
          : poiStore.state.poiPendingReason?.length > 0
          ? poiStore.state.poiPendingReason
          : ''
        : '';
    },
    poiRecordId() {
      return poiStore.state.poiRecordId;
    },
    poiPendingRecord() {
      return poiStore.state.poiPendingRecord;
    },
    idInformationForm() {
      return poiStore.state.idInformationForm;
    },
    prevExistingIdFilePathList() {
      return poiStore.state.prevExistingIdFilePathList;
    },
    poiAuditStatus() {
      return this.$store.state.common.poiAuditStatus;
    },
    isBackImageRequired() {
      return this.idTypeData !== idType.passport;
    },
    ...mapState('register', ['currStep']),
    ...mapGetters('common', ['isPoiResubmit'])
  },
  watch: {
    idTypeData() {
      this.$refs.form?.clearValidate();
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const response = await poiStore.dispatch('actionGetData');
      if (response instanceof Error) {
        this.$message({
          message: response.message,
          type: 'error'
        });
        this.$router.push(RegisterRoutePath.register);
      }
      if (this.poiAuditStatus === auditStatus.pending) poiStore.dispatch('setIdInformationForm', this.poiPendingRecord);
    },
    backButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV2_ID_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV2_BACK
      })
      this.$store.dispatch('register/setActiveTab', registerStep.idInformation);
    },
    async nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV2_ID_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV2_NEXT
      })
      this.$refs.form?.validate(async valid => {
        if (valid) {
          const { frontImage, backImage } = this.$refs?.form?.form;

          startLoading('ajax');
          const uploadImagesResponses = await Promise.all([
            commonRepository.uploadFile(frontImage),
            backImage !== null ? commonRepository.uploadFile(backImage) : null
          ]);
          const frontImageResponse = uploadImagesResponses[0];
          const backImageResponse = uploadImagesResponses?.[1] ?? null;

          if (frontImageResponse instanceof Error) {
            this.$message({
              message: frontImageResponse.message,
              type: 'error'
            });
          } else if (backImageResponse instanceof Error) {
            this.$message({
              message: backImageResponse.message,
              type: 'error'
            });
          } else {
            let response = new Error();
            if (this.isPoiResubmit) {
              response = await registerRepository.sendVerification({
                filePathList: [frontImageResponse, ...(backImageResponse !== null ? [backImageResponse] : [])],
                fileType: fileType.poi,
                auditType: auditType.general,
                fileId: this.poiRecordId,
                isPending: this.poiAuditStatus === auditStatus.pending,
                prevExistingFilePathList: this.prevExistingIdFilePathList
              });
            } else {
              response = await registerRepository.process({
                step: getUserStep(registerStep.idUpload),
                form: {
                  ...this.idInformationForm,
                  idDocFilePathList: [frontImageResponse, ...(backImageResponse !== null ? [backImageResponse] : [])]
                }
              });
            }

            if (response instanceof Error) {
              this.$message({
                message: response.message,
                type: 'error'
              });
            } else {
              if (this.currStep < registerStep.poaInformation) {
                await this.$store.dispatch('register/setStep', registerStep.poaInformation);
              }

              this.$store.commit('common/setPoiAuditStatus', auditStatus.submitted);
              this.$store.dispatch('register/setActiveTab', registerStep.idStatus);
              await this.$store.dispatch('common/actionPoiAuditStatus');
              if (this.poiAuditStatus === auditStatus.completed) {
                this.$store.dispatch('register/setActiveTab', registerStep.poaInformation);
              }
              this.$router.push(RegisterRoutePath.identityVerificationComplete);
            }
          }

          endAjaxLoading();
        } else {
          console.log('error submit!!');
        }
      });
    }
  }
};
</script>
